import { memo, useCallback, useMemo } from 'react';

import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import useComputedRegistrationSettings from '../../hooks/use-computed-registration-settings';
import { SingleSignOnTypes } from '@/types/registration.types';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import useLoadSSOs from '../../hooks/use-load-ssos';
import { selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import CustomSelect from '@ui/select/custom-select/custom-select';
import { SignInOption } from './sign-in-options/sign-in-option-types';

const SSORegistrationSettingsFields = memo(() => {
  const dispatch = useAppDispatch();
  const savingWorkingProject = useAppSelector(selectIsSavingWorkingProject);
  const { ssos, publicOauthIntegration } = useLoadSSOs();
  const { registrationSettings } = useComputedRegistrationSettings();

  const customSignInOptions: SignInOption[] = useMemo(() => {
    return (
      ssos
        ?.filter((sso) => sso.enabled === true)
        .map((sso) => {
          return { type: sso.type as SingleSignOnTypes, label: sso.login_text, icon: sso.icon, uuid: sso.uuid };
        }) ?? []
    );
  }, [ssos]);

  const oauthSignInOption: SignInOption | null = useMemo(() => {
    return publicOauthIntegration
      ? {
          type: SingleSignOnTypes.oAuth,
          label: publicOauthIntegration.login_text || '',
          icon: publicOauthIntegration.icon || '',
          value: SingleSignOnTypes.oAuth,
        }
      : null;
  }, [publicOauthIntegration]);

  const selectOptions: TReactSelectOption<string, string, SignInOption>[] = useMemo(() => {
    return [
      ...(customSignInOptions || []).map((option) => ({
        label: option.label,
        value: option.uuid ?? option.type,
        meta: option,
      })),
      oauthSignInOption
        ? {
            label: oauthSignInOption.label,
            value: oauthSignInOption.value,
            meta: oauthSignInOption,
          }
        : undefined,
    ].filter((option) => !!option?.value) as TReactSelectOption<string, string, SignInOption>[];
  }, [customSignInOptions, oauthSignInOption]);

  const currentOption = useMemo(() => {
    return selectOptions.find((option) => {
      const value = option.value;
      const isOn = registrationSettings?.singleSignOn?.singleSignOns?.includes(value) ?? false;
      return isOn;
    });
  }, [registrationSettings?.singleSignOn?.singleSignOns, selectOptions]);

  const handleToggleSignInOption = useCallback(
    (value: string, on: boolean) => {
      if (!registrationSettings) return;
      // Only 1 can be on at a time, if it's already on, turn it off
      const singleSignOns = on ? [value] : [];
      dispatch(
        saveWorkingProjectByFields({
          registration_settings: {
            ...registrationSettings,
            singleSignOn: {
              ...(registrationSettings?.singleSignOn ?? { isOn: false, displayTop: true }),
              singleSignOns,
            },
          },
        })
      );
    },
    [dispatch, registrationSettings]
  );

  const handleSelectOption = useCallback(
    (selectedOptions: TReactSelectOption[]) => {
      const selectedOption = selectedOptions?.[0];
      const isOn = !!selectedOption?.value;
      /* safe to fallback to empty string to satisfy TS because handleToggleSignInOption explicitly checks for isOn */
      const value = (selectedOption?.value || '').toString();
      handleToggleSignInOption(value, isOn);
    },
    [handleToggleSignInOption]
  );

  return (
    <Container design="GlobalCont1" className="flex fd-column gap-8" borderRadius="12px" padding="16px">
      <div className="flex ai-center jc-space-between">
        <Text tag="h6" className="H6" design="GlobalIcoText1">
          SSO Protocol
        </Text>
        <Container width="212px">
          <CustomSelect
            id="sso-registration-select"
            options={selectOptions}
            controlledOptions={currentOption ? [currentOption] : []}
            isMultiSelect={false}
            hideHeader
            labelHeight="32px"
            permanentDropdownIcon
            Placeholder="SSO Protocol"
            onChange={handleSelectOption}
            disabled={!selectOptions?.length || savingWorkingProject}
            RenderUnfocusedLabelContent={({ option: { label } }) => {
              return (
                <Container height="100%" className="flex ai-center">
                  <Text maxWidth="100%" whiteSpace="normal" wordBreak="break-all" clamp={1}>
                    {label}
                  </Text>
                </Container>
              );
            }}
          />
        </Container>
      </div>
    </Container>
  );
});

SSORegistrationSettingsFields.displayName = 'SSORegistrationSettingsFields';

export default SSORegistrationSettingsFields;
